import { type KeyboardEvent, useCallback, useMemo } from 'react';

import type { ComboboxOption, ComboboxProps } from './types';
import { SelectType } from './types';

/**
 * Comboboxの内部ロジックを管理するカスタムフック
 *
 * @param props Comboboxのプロパティ
 * @returns 選択状態と操作メソッド
 */
export function useCombobox<TOption extends ComboboxOption>(
  props: ComboboxProps<TOption>,
) {
  /**
   * 現在選択されているオプションのSetを生成
   * 単一選択の場合は配列に変換して管理
   */
  const selectedOptions = useMemo(
    () =>
      new Set(props.type === SelectType.multiple ? props.value : [props.value]),
    [props.type, props.value],
  );

  /**
   * オプションのリストをメモ化
   * - props.optionsが変更された場合にのみ再計算
   */
  const memoizedOptions = useMemo(() => props.options, [props.options]);

  /**
   * オプションが選択された時の処理
   * - 単一選択: 現在の値と同じ場合はクリア、異なる場合は選択
   * - 複数選択: 既に選択されている場合は削除、そうでない場合は追加
   */
  const onSelect = useCallback(
    (value: string): void => {
      if (props.type === SelectType.multiple) {
        const multiProps = props as Extract<
          typeof props,
          { type: typeof SelectType.multiple }
        >;
        multiProps.onValueChange(
          selectedOptions.has(value)
            ? multiProps.value.filter((v: string) => v !== value)
            : [...multiProps.value, value],
        );
      } else {
        const singleProps = props as Extract<
          typeof props,
          { type: typeof SelectType.single }
        >;
        singleProps.onValueChange(value === singleProps.value ? null : value);
      }

      // 選択された値をキャッシュに保存
      const option = memoizedOptions.find((opt) => opt.value === value);
      if (!option) throw new Error(`Option with value ${value} not found`);
      props.valueToLabelCache?.set(value, option.label);
    },
    [props, memoizedOptions, selectedOptions],
  );

  /**
   * すべての選択をクリアする
   */
  const onClearAll = useCallback(() => {
    if (props.type === SelectType.multiple) {
      (
        props as Extract<typeof props, { type: typeof SelectType.multiple }>
      ).onValueChange([]);
    } else {
      (
        props as Extract<typeof props, { type: typeof SelectType.single }>
      ).onValueChange(null);
    }
  }, [props]);

  /**
   * キーボード入力時の処理
   * 複数選択時、入力欄が空でBackspaceが押された場合に最後の選択を削除
   */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const shouldRemoveLastOption =
        event.currentTarget.value === '' &&
        props.type === SelectType.multiple &&
        event.key === 'Backspace' &&
        (props as Extract<typeof props, { type: typeof SelectType.multiple }>)
          .value.length > 0;

      if (shouldRemoveLastOption) {
        const multiProps = props as Extract<
          typeof props,
          { type: typeof SelectType.multiple }
        >;
        multiProps.onValueChange(multiProps.value.slice(0, -1));
      }
    },
    [props],
  );

  return {
    selectedOptions,
    onSelect,
    onClearAll,
    onKeyDown,
  } as const;
}
